/**
 * product-form — pont Dawn → moteur panier Traction.
 *
 * Utilisé par le quick-add des cartes produit (snippets/card-product.liquid) et les
 * formulaires produit Dawn encore présents. Le formulaire POSTe lui-même sur
 * routes.cart_add_url (FormData, sans JS il poste nativement sur /cart/add), puis
 * délègue au moteur :
 *   document → tr:cart-add  { items, cart, source: 'quick-add' }
 *   document → tr:cart-open (mode tiroir) — ou redirection vers /cart (mode page).
 *
 * API conservée pour product-info.js (Dawn) : toggleSubmitButton(), handleErrorMessage(),
 * variantIdInput, submitButton, submitButtonText.
 */

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton?.querySelector('span') ?? null;
        this.hideErrors = this.dataset.hideErrors === 'true';
        this.busy = false;

        if (this.form === null) return;

        if (this.variantIdInput !== null) this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));

        if (this.cartType === 'drawer' && this.submitButton !== null) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }
      }

      get cartType() {
        return window.Traction?.cartType ?? 'drawer';
      }

      get variantIdInput() {
        return this.form?.querySelector('[name=id]') ?? null;
      }

      get addRoute() {
        const base = window.Traction?.routes?.cart_add_url ?? window.routes?.cart_add_url;
        if (typeof base === 'string' && base !== '') return base.endsWith('.js') ? base : `${base}.js`;

        const root = window.Shopify?.routes?.root ?? '/';
        return `${root}cart/add.js`.replace('//', '/');
      }

      get cartRoute() {
        const base = window.Traction?.routes?.cart_url ?? window.routes?.cart_url;
        if (typeof base === 'string' && base !== '') return base;

        const root = window.Shopify?.routes?.root ?? '/';
        return `${root}cart`.replace('//', '/');
      }

      async onSubmitHandler(event) {
        event.preventDefault();

        if (this.busy) return;
        if (this.submitButton?.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();
        this.setBusy(true);

        const formData = new FormData(this.form);
        const variantId = Number(formData.get('id'));
        const quantity = Number(formData.get('quantity')) || 1;

        try {
          const response = await fetch(this.addRoute, {
            method: 'POST',
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: formData,
          });

          const payload = await response.json().catch(() => ({}));

          if (response.ok === false || payload.status !== undefined) {
            const message = payload.description ?? payload.message ?? window.Traction?.strings?.cartError ?? '';
            this.handleErrorMessage(message);
            this.markSoldOutIfNeeded(payload);

            document.dispatchEvent(
              new CustomEvent('tr:cart-error', {
                bubbles: true,
                detail: {
                  message,
                  status: Number(payload.status) || response.status,
                  source: 'quick-add',
                  cart: null,
                },
              })
            );
            return;
          }

          const cart = await this.fetchCart();
          const items = Array.isArray(payload.items)
            ? payload.items.map((item) => ({ id: item.variant_id ?? item.id, quantity: item.quantity }))
            : [{ id: variantId, quantity }];

          document.dispatchEvent(
            new CustomEvent('tr:cart-add', {
              bubbles: true,
              detail: { items, cart, source: 'quick-add' },
            })
          );

          this.afterAdd();
        } catch (error) {
          const message = window.Traction?.strings?.networkError ?? String(error);
          this.handleErrorMessage(message);

          document.dispatchEvent(
            new CustomEvent('tr:cart-error', {
              bubbles: true,
              detail: { message, status: 0, source: 'quick-add', cart: null, error: String(error) },
            })
          );
        } finally {
          this.setBusy(false);
        }
      }

      afterAdd() {
        /* Mode page : on va au panier (comportement Dawn conservé). */
        if (this.cartType === 'page') {
          window.location.assign(this.cartRoute);
          return;
        }

        /* W-E : la modale Dawn `<quick-add-modal>` n'existe plus (réécriture W-C) —
           le panneau d'ajout rapide Traction est `<tr-quick-add>`, un `<dialog>`. */
        const quickAdd = this.closest('dialog.tr-quick-add-modal');
        if (quickAdd !== null && quickAdd.open) quickAdd.close();

        document.dispatchEvent(new CustomEvent('tr:cart-open', { bubbles: true }));
      }

      async fetchCart() {
        try {
          const response = await fetch(`${this.cartRoute}.js`, { headers: { Accept: 'application/json' } });
          if (response.ok === false) return null;
          return await response.json();
        } catch {
          return null;
        }
      }

      setBusy(state) {
        this.busy = state;

        if (this.submitButton === null) return;

        this.submitButton.classList.toggle('loading', state);
        this.submitButton.setAttribute('aria-busy', String(state));

        if (state) {
          this.submitButton.setAttribute('aria-disabled', 'true');
        } else if (this.error !== true) {
          this.submitButton.removeAttribute('aria-disabled');
        }

        const spinner = this.querySelector('.loading__spinner');
        if (spinner !== null) spinner.classList.toggle('hidden', state === false);
      }

      markSoldOutIfNeeded(payload) {
        const soldOutMessage = this.submitButton?.querySelector('.sold-out-message');
        if (soldOutMessage === null || soldOutMessage === undefined) return;
        if (Number(payload.status) !== 422) return;

        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButtonText?.classList.add('hidden');
        soldOutMessage.classList.remove('hidden');
        this.error = true;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;

        this.errorMessage =
          this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage && this.errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (this.submitButton === null) return;

        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text && this.submitButtonText) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          if (this.submitButtonText) {
            this.submitButtonText.textContent =
              window.variantStrings?.addToCart ?? window.Traction?.strings?.addToCart ?? '';
          }
        }
      }
    }
  );
}
